import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../css/AvaliacaoRestaurante.css';
import { useAvaliacao } from "../context/AvaliacaoRestauranteContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AvaliacaoRestaurante = () => {
  const { salvarAvaliacao } = useAvaliacao();
  const { id } = useParams();
  const navigate = useNavigate();
  const [nomeCliente, setNomeCliente] = useState('');

  const [avaliacoes, setAvaliacoes] = useState({
    ambiente: { rating: 0, comment: '' },
    comida: { rating: 0, comment: '' },
    atendimento: { rating: 0, comment: '' },
    preco: { rating: 0, comment: '' }
  });
  
  const [hoveredRating, setHoveredRating] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const categorias = [
    {
      key: 'ambiente',
      titulo: 'Ambiente',
      descricao: 'Avalie a decoração, conforto, limpeza e atmosfera do local',
      icon: '🏠',
      labels: ['Péssimo', 'Ruim', 'Regular', 'Bom', 'Excelente']
    },
    {
      key: 'comida',
      titulo: 'Comida',
      descricao: 'Avalie o sabor, apresentação e qualidade dos pratos',
      icon: '🍽️',
      labels: ['Péssima', 'Ruim', 'Regular', 'Boa', 'Excelente']
    },
    {
      key: 'atendimento',
      titulo: 'Atendimento',
      descricao: 'Avalie a cordialidade, rapidez e atenção dos funcionários',
      icon: '👨‍🍳',
      labels: ['Péssimo', 'Ruim', 'Regular', 'Bom', 'Excelente']
    },
    {
      key: 'preco',
      titulo: 'Custo-Benefício',
      descricao: 'Avalie se os preços são justos pela qualidade oferecida',
      icon: '💰',
      labels: ['Muito caro', 'Caro', 'Justo', 'Bom', 'Ótimo']
    }
  ];

  const handleRating = (categoria, rating) => {
    setAvaliacoes(prev => ({
      ...prev,
      [categoria]: { ...prev[categoria], rating }
    }));
  };

  const handleComment = (categoria, comment) => {
    setAvaliacoes(prev => ({
      ...prev,
      [categoria]: { ...prev[categoria], comment }
    }));
  };

  const handleMouseEnter = (categoria, rating) => {
    setHoveredRating(prev => ({ ...prev, [categoria]: rating }));
  };

  const handleMouseLeave = (categoria) => {
    setHoveredRating(prev => ({ ...prev, [categoria]: 0 }));
  };

  const calcularMediaGeral = () => {
    const ratings = Object.values(avaliacoes).map(a => a.rating);
    const soma = ratings.reduce((acc, curr) => acc + curr, 0);
    const avaliacoesPreenchidas = ratings.filter(r => r > 0).length;
    return avaliacoesPreenchidas > 0 ? (soma / avaliacoesPreenchidas).toFixed(1) : '0.0';
  };

  const todasAvaliacoesPreenchidas = () => {
    return Object.values(avaliacoes).every(a => a.rating > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!todasAvaliacoesPreenchidas()) {
      toast.warning('Por favor, avalie todas as categorias antes de enviar.');
      return;
    }

    setIsSubmitting(true);
    
    const payload = {
      nomeCliente: nomeCliente.trim() || "Anônimo",
      avaliacoes: {
          ambiente: {
             rating: avaliacoes.ambiente.rating,
             comment: avaliacoes.ambiente.comment,
          },
          comida: {
             rating: avaliacoes.comida.rating,
             comment: avaliacoes.comida.comment,
          },
          atendimento: {
             rating: avaliacoes.atendimento.rating,
             comment: avaliacoes.atendimento.comment,
          },
          preco: {
             rating: avaliacoes.preco.rating,
             comment: avaliacoes.preco.comment,
          },
      },
      mediaGeral: parseFloat(calcularMediaGeral()),
      dataAvaliacao: new Date().toISOString(),
      restauranteId: id
    };

    try {
      await salvarAvaliacao(payload, id);
      toast.success('Avaliação enviada com sucesso!');
      setSubmitted(true);
    } catch (error) {
      console.error('Erro ao enviar avaliação:', error);
      toast.error(error.message || 'Erro ao enviar avaliação. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="avaliacao-container">
        <ToastContainer />
        <div className="sucesso-card">
          <div className="sucesso-icon">✓</div>
          <h2>Obrigado pela sua avaliação!</h2>
          <p>Sua opinião é muito importante para melhorarmos nossos serviços.</p>
          <div className="media-final">
            <span className="media-label">Sua nota geral:</span>
            <span className="media-valor">{calcularMediaGeral()}</span>
            <div className="estrelas-media">
              {[1, 2, 3, 4, 5].map(star => (
                <span 
                  key={star} 
                  className={`star ${star <= Math.round(calcularMediaGeral()) ? 'filled' : ''}`}
                >
                  ★
                </span>
              ))}
            </div>
          </div>
          <button 
            className="btn-voltar"
            onClick={() => navigate('/restaurantes')}
          >
            Voltar aos restaurantes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="avaliacao-container">
      <ToastContainer />
      <div className="avaliacao-header">
        <h1>Avalie sua experiência</h1>
        <p>Sua opinião nos ajuda a melhorar cada vez mais</p>
      </div>

      <div className="media-geral-card">
        <span className="media-label">Nota geral</span>
        <span className="media-valor">{calcularMediaGeral()}</span>
        <div className="estrelas-media">
          {[1, 2, 3, 4, 5].map(star => (
            <span 
              key={star} 
              className={`star ${star <= Math.round(calcularMediaGeral()) ? 'filled' : ''}`}
            >
              ★
            </span>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="avaliacao-form">
        <div className="nome-avaliador-card">
          <label htmlFor="nomeCliente" className="nome-label">
            <span className="nome-icon">👤</span>
            Como gostaria de ser identificado? (opcional)
          </label>
          <input
            type="text"
            id="nomeCliente"
            className="nome-input"
            placeholder="Digite seu nome ou deixe em branco para avaliar anonimamente"
            value={nomeCliente}
            onChange={(e) => setNomeCliente(e.target.value)}
            maxLength={50}
          />
        </div>
        {categorias.map((categoria) => {
          const currentRating = avaliacoes[categoria.key].rating;
          const hovered = hoveredRating[categoria.key] || 0;
          const displayRating = hovered || currentRating;

          return (
            <div key={categoria.key} className="categoria-card">
              <div className="categoria-header">
                <span className="categoria-icon">{categoria.icon}</span>
                <div className="categoria-info">
                  <h3>{categoria.titulo}</h3>
                  <p>{categoria.descricao}</p>
                </div>
              </div>

              <div className="rating-section">
                <div className="stars-container">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      type="button"
                      key={star}
                      className={`star-btn ${star <= displayRating ? 'active' : ''}`}
                      onClick={() => handleRating(categoria.key, star)}
                      onMouseEnter={() => handleMouseEnter(categoria.key, star)}
                      onMouseLeave={() => handleMouseLeave(categoria.key)}
                      aria-label={`${star} estrelas`}
                    >
                      <span className="star-icon">★</span>
                    </button>
                  ))}
                </div>
                {displayRating > 0 && (
                  <span className="rating-label">
                    {categoria.labels[displayRating - 1]}
                  </span>
                )}
              </div>

              <textarea
                className="comment-input"
                placeholder={`Conte-nos mais sobre ${categoria.titulo.toLowerCase()} (opcional)`}
                value={avaliacoes[categoria.key].comment}
                onChange={(e) => handleComment(categoria.key, e.target.value)}
                rows={3}
              />
            </div>
          );
        })}

        <button 
          type="submit" 
          className={`btn-submit ${!todasAvaliacoesPreenchidas() ? 'disabled' : ''}`}
          disabled={isSubmitting || !todasAvaliacoesPreenchidas()}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              Enviando...
            </>
          ) : (
            'Enviar Avaliação'
          )}
        </button>
        <button 
          className="btn-voltar"
          onClick={() => navigate('/restaurantes')}
        >
          Voltar
        </button>
        <br/>

        {!todasAvaliacoesPreenchidas() && (
          <p className="aviso-campos">
            * Avalie todas as categorias para enviar
          </p>
        )}

      </form>
    </div>
  );
};

export default AvaliacaoRestaurante;